import { api } from '../../core/network/api';

/**
 * Thin REST wrappers for the world map screen. No game logic lives here —
 * these functions only shape requests/responses; the server is authoritative
 * for everything (grid contents, claim validity, expiry).
 */

export type PoiKind = 'COLONY_PLOT' | 'RESOURCE_NODE' | 'DECORATION';
export type ResourceType = 'REGOLITH' | 'WATER_ICE' | 'HE3' | 'COMPOSITE' | 'CRIMSON_DUST';
export type DecorationKind = 'MOUNTAIN' | 'CRATER' | 'DUNE';

export interface WorldMapPoi {
  id: string;
  kind: PoiKind;
  x: number;
  y: number;
  colonyId: string | null;
  resourceType: ResourceType | null;
  /** Set for DECORATION POIs — a purely visual, never-claimable landmark. */
  decorationKind?: DecorationKind | null;
  /**
   * The occupying colony's Bastion Wall level, null for an uncolonized POI.
   * Optional so older cached responses / test fixtures without this field
   * still type-check — resolveCellVisual treats "absent" the same as null.
   */
  wallLevel?: number | null;
  /**
   * The occupying colony's Command Center ("HQ") level, null for an
   * uncolonized POI — picks which settlement render the plot draws (see
   * COLONY_MAP_ART_TIERS). Optional for the same reason as wallLevel above.
   */
  hqLevel?: number | null;
  /**
   * The following four fields are only ever present on colonized COLONY_PLOT
   * POIs. All optional (rather than required + nullable) for the same reason
   * as wallLevel above — existing test fixtures across this module construct
   * WorldMapPoi literals without them, and they still need to type-check.
   */
  colonyName?: string | null;
  ownerUsername?: string | null;
  /** The owner's equipped cosmetic title, already resolved to its display label by the server. */
  ownerTitle?: string | null;
  /** Rarity group of that title, for the badge style. */
  ownerTitleRarity?: string | null;
  colonyStatus?: 'ACTIVE' | 'ABANDONED' | null;
  isBot?: boolean;
  /** True origin marker for a true barbarian (seeded or decayed-from-abandonment colony). */
  isBarbarian?: boolean | null;
  /** Which barbarian fort art variant (frontend/public/barbarian/<id>.webp) to render — set for both true barbarians and bot camps, null for a real player colony. Assigned once, at random, at creation/conversion time and stored permanently. */
  barbarianFortArtId?: string | null;
}

export interface MyColony {
  id: string;
  /** The world this colony sits in — every map read is scoped to it. */
  worldId: string;
}

export interface WorldMapBounds {
  minX?: number;
  maxX?: number;
  minY?: number;
  maxY?: number;
}

/**
 * GET /worldmap — `worldId` is required (there is no cross-world map view);
 * the four bounds are optional, and omitting them returns that world's whole
 * 80x40 grid.
 */
export async function fetchWorldMap(worldId: string, bounds?: WorldMapBounds): Promise<WorldMapPoi[]> {
  const params = new URLSearchParams({ worldId });
  if (bounds?.minX !== undefined) params.set('minX', String(bounds.minX));
  if (bounds?.maxX !== undefined) params.set('maxX', String(bounds.maxX));
  if (bounds?.minY !== undefined) params.set('minY', String(bounds.minY));
  if (bounds?.maxY !== undefined) params.set('maxY', String(bounds.maxY));
  return api.get<WorldMapPoi[]>(`/worldmap?${params.toString()}`);
}

/** GET /me/colony — only the caller's own colonyId is needed here. */
export async function fetchMyColony(): Promise<MyColony> {
  return api.get<MyColony>('/me/colony');
}

